// app/routes/index.tsx
import { useLoaderData, useNavigate, useSearchParams } from '@remix-run/react';
import { LoaderFunctionArgs, json } from '@remix-run/server-runtime';
import { useEffect, useMemo, useState } from 'react';
import { ProfileCompletionModal } from '~/components/account/ProfileCompletionModal';
import { CategoriesSection } from '~/components/collections/CategoriesSection';
import { HeroCarousel } from '~/components/home/HeroCarousel';
import { RedtabPickSection } from '~/components/home/RedtabPickSection';
import { Sidebar } from '~/components/home/Sidebar';
import { SuperSaverSection } from '~/components/home/SuperSaverSection';
import type { Collection } from '~/generated/graphql';
import { SortOrder } from '~/generated/graphql';
import { getActiveCustomer } from '~/providers/account/account';
import { getCollections } from '~/providers/collections/collections';
import { getAllProducts } from '~/providers/products/products';
import { KYCStatus } from './account.kyc';

export async function loader({ request }: LoaderFunctionArgs) {
  const collections = await getCollections(request);
  const products = await getAllProducts(
    { take: 24, sort: { updatedAt: SortOrder.Desc } },
    request,
  );

  const activeCustomer = await getActiveCustomer({ request });

  return json({
    collections: collections || [],
    products: products || [],
    activeCustomer,
  });
}

export default function Index() {
  const { products, collections, activeCustomer } = useLoaderData<typeof loader>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [showProfileModal, setShowProfileModal] = useState(false);

  // Check for profile completion modal trigger from URL
  useEffect(() => {
    const showKYCParam = searchParams.get('showKYC');
    const kycStatus = activeCustomer?.customFields?.kycStatus;

    if (showKYCParam === 'true' && kycStatus !== KYCStatus.Verified) {
      // User requested to hide this popup
      // setShowProfileModal(true);
      searchParams.delete('showKYC');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, activeCustomer]);

  const handleProfileCompletion = () => {
    setShowProfileModal(false);
  };

  const handleProfileSkip = () => {
    setShowProfileModal(false);
  };

  return (
    <>
      <ProfileCompletionModal
        isOpen={showProfileModal}
        onClose={handleProfileCompletion}
        onSkip={handleProfileSkip}
        isProfileComplete={!!(activeCustomer?.firstName && activeCustomer?.lastName)}
      />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 text-gray-900">
        {/* Main Layout Container with Sidebar */}
        <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex gap-6">
            {/* Left Sidebar - Hidden on mobile */}
            <Sidebar collections={collections as Collection[]} />

            {/* Main Content Area */}
            <div className="flex-1 min-w-0">
              {/* Hero Carousel */}
              <div className="mb-4 sm:mb-6">
                <HeroCarousel />
              </div>

              {/* Categories Section - Mobile only horizontal scroll, desktop shows grid */}
              <CategoriesSection collections={collections} />

              {/* Super Saver Section */}
              <SuperSaverSection products={products.slice(0, 6)} />

              {/* Redtab Pick Section - Product Grid */}
              <RedtabPickSection products={products} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
